import { useEffect, useState } from "react";

import { useUiStore } from "../store/uiStore";

/**
 * The three platforms of the platform law (PROJECT-KNOWLEDGE.md §The platform law).
 * Nothing platform-shaped is ever written to the query cache or to a store: the
 * override is a user preference, the rest is measured live from the medium.
 */
export type Platform = "desktop" | "tablet" | "phone";

/** Whether the resolved platform came from the user's override or from the medium. */
export type PlatformSource = "override" | "heuristic";

/**
 * The PRIMARY pointer, never `any-pointer`: a touchscreen laptop with a mouse
 * reads desktop.
 */
const COARSE = "(pointer: coarse)";

/**
 * Both clauses matter. `max-height` keeps a phone held in landscape — wide but
 * short — out of the tablet tree.
 */
const SMALL = "(max-width: 767px), (max-height: 500px)";

function matches(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(query).matches;
}

/** The heuristic, verbatim: phone ⇔ coarse AND small; tablet ⇔ coarse; else desktop. */
export function heuristicPlatform(): Platform {
  const coarse = matches(COARSE);
  if (!coarse) return "desktop";
  return matches(SMALL) ? "phone" : "tablet";
}

/**
 * Live listeners on both queries, so crossing a boundary swaps the page tree in
 * place rather than at the next reload.
 */
function useHeuristicPlatform(): Platform {
  // SSR and the first client render agree on `desktop`; the effect corrects it
  // before paint-relevant work, which keeps hydration stable.
  const [platform, setPlatform] = useState<Platform>("desktop");

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const coarse = window.matchMedia(COARSE);
    const small = window.matchMedia(SMALL);
    const read = () => setPlatform(heuristicPlatform());
    read();
    coarse.addEventListener("change", read);
    small.addEventListener("change", read);
    return () => {
      coarse.removeEventListener("change", read);
      small.removeEventListener("change", read);
    };
  }, []);

  return platform;
}

/**
 * The one platform resolver.
 *
 * Order, verbatim:
 *   1. `uiStore.platformOverride` when it is not `'auto'`;
 *   2. else the heuristic above.
 */
export function usePlatform(): Platform {
  const override = useUiStore((s) => s.platformOverride);
  const heuristic = useHeuristicPlatform();
  return override === "auto" ? heuristic : override;
}

/** Which of the two branches answered — read by the account menu and /dev/coverage. */
export function usePlatformSource(): PlatformSource {
  const override = useUiStore((s) => s.platformOverride);
  return override === "auto" ? "heuristic" : "override";
}
